import classNames from 'classnames';
import Button from 'components/button';
import {Field, FileInput, Form, Input} from 'components/forms';
import {gt} from 'components/forms/form/validators';
import Spinner from 'components/spinner';
import {autobind} from 'core-decorators';
import {isSessionSupervisor} from 'core/utils';
import addNoteMutation from 'graphql/mutations/orders/add-note.gql';
import approveInvoiceMutation from 'graphql/mutations/orders/approve-invoice.gql';
import approveOrderMutation from 'graphql/mutations/orders/approve-order.gql';
import approveRefundMutation from 'graphql/mutations/orders/approve-refund.gql';
import cancelOrderMutation from 'graphql/mutations/orders/cancel-order.gql';
import rejectInvoiceMutation from 'graphql/mutations/orders/reject-invoice.gql';
import rejectOrderMutation from 'graphql/mutations/orders/reject-order.gql';
import submitInvoiceMutation from 'graphql/mutations/orders/submit-invoice.gql';
import userQuery from 'graphql/queries/user.gql';
import PropTypes from 'prop-types';
import React, {Component, Fragment} from 'react';
import {compose, graphql} from 'react-apollo';
import styles from './styles.css';

@compose(
    graphql(userQuery, {name: 'userData'}),
    graphql(approveOrderMutation, {
        props: ({mutate}) => ({
            approveOrder: variables => mutate({variables}),
        }),
    }),
    graphql(rejectOrderMutation, {
        props: ({mutate}) => ({
            rejectOrder: variables => mutate({variables}),
        }),
    }),
    graphql(cancelOrderMutation, {
        props: ({mutate}) => ({
            cancelOrder: variables => mutate({variables}),
        }),
    }),
    graphql(approveRefundMutation, {
        props: ({mutate}) => ({
            approveRefund: variables => mutate({variables}),
        }),
    }),
    graphql(submitInvoiceMutation, {
        props: ({mutate}) => ({
            submitInvoice: variables => mutate({variables}),
        }),
    }),
    graphql(approveInvoiceMutation, {
        props: ({mutate}) => ({
            approveInvoice: variables => mutate({variables}),
        }),
    }),
    graphql(rejectInvoiceMutation, {
        props: ({mutate}) => ({
            rejectInvoice: variables => mutate({variables}),
        }),
    }),
    graphql(addNoteMutation, {
        props: ({mutate}) => ({
            addNote: variables => mutate({variables}),
        }),
    }),
)
@autobind
export default class OrderStatus extends Component {
    static propTypes = {
        actions: PropTypes.array.isRequired,
        order: PropTypes.object.isRequired,
        onStatusChange: PropTypes.func,
    };

    state = {
        formId: 1,
    };

    async runMutation(action, variables = {}) {
        this.setState({loading: true, errors: undefined, statusTo: action.status_to});
        const mutation = this.props[action.mutation];
        const response = await mutation({order: this.props.order.id, ...variables});

        if (response.data.action.errors) {
            this.setState({
                loading: false,
                errors: response.data.action.errors,
                statusTo: null,
            });
        } else {
            await this.props.onStatusChange();
            this.setState({loading: false, formId: this.state.formId + 1, statusTo: null});
        }
    }

    getExtraFields() {
        const statuses = ['pending_invoice', 'invoice_rejected'];
        if (statuses.includes(this.props.order.status.id) && !isSessionSupervisor(this.props.userData.user)) {
            return [
                {
                    key: 'invoices',
                    label: 'Factura/Evidencia de evento',
                    input: props => <FileInput multiple {...props} disabled={this.state.loading} />,
                },
                {
                    key: 'confirmedAmount',
                    label: 'Confirmar monto',
                    validators: [gt(0)],
                    input: props => <Input type='number' {...props} disabled={this.state.loading} />,
                },
            ];
        }
        return [];
    }

    renderActions(fields = []) {
        const {order, userData: {user}} = this.props;

        const positive = this.props.actions.filter(action => action.type === 'positive');
        const negative = this.props.actions.filter(action => action.type === 'negative');

        const notesLabel = isSessionSupervisor(user)
            ? 'Comentarios para la organización'
            : 'Comentarios para Nidera';

        const onConfirmation = (action, values) => {
            if (window.confirm('¿Estás seguro de que querés continuar?')) {
                this.runMutation(action, values);
            }
        };

        const onSubmit = values => onConfirmation(
            (positive.length && positive[0]) || (negative.length && negative[0]),
            values,
        );

        const handleOnClick = (action, values) => {
            return () => onConfirmation(action, values);
        };

        const status = order.status.id;

        return (
            <Form
                key={this.state.formId}
                fields={[
                    ...fields.map(({key, label, required, validators}) => ({key, label, required, validators})),
                    {key: 'notes', required: false},
                ]}
                loading={this.state.loading}
                errors={this.state.errors}
                onSubmit={onSubmit}>
                {({getFieldProps, renderErrors, isValid, values}) => (
                    <Fragment>
                        {fields.map(field => {
                            const FieldInput = field.input;
                            return (
                                <Field key={field.key} label={field.label}>
                                    <FieldInput {...getFieldProps(field.key)} />
                                    {renderErrors(field.key)}
                                </Field>
                            );
                        })}
                        <Field label={notesLabel}>
                            <Input type='text' {...getFieldProps('notes')} disabled={this.state.loading} />
                            {renderErrors('notes')}
                        </Field>
                        {renderErrors('__all__')}
                        <Form.Actions>
                            {this.props.actions.map(action => (
                                <Button
                                    key={action.status_to}
                                    theme={action.type === 'positive' ? 'yellow' : 'red'}
                                    type='button'
                                    onClick={handleOnClick(action, values)}
                                    disabled={this.state.loading || (action.type === 'positive' && !isValid())}>
                                    {action.name}
                                </Button>
                            ))}
                        </Form.Actions>
                        {this.state.loading
                            && this.state.statusTo === 'invoice_submitted'
                            && ['pending_invoice', 'invoice_rejected'].includes(status)
                            && (
                                <p className={styles.uploading}>
                                    Los archivos se están subiendo. Por favor esperá un momento.
                                </p>
                            )}
                    </Fragment>
                )}
            </Form>
        );
    }

    render() {
        if (this.props.userData.loading) return <Spinner />;

        const extraFields = this.getExtraFields();
        return (
            <div className={styles.root}>
                <div className={styles.cardHeader}>Acciones</div>
                <div className={classNames(styles.cardBody, styles.cardBodyRefund)}>
                    {this.renderActions(extraFields)}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./views/order-detail/order-status/index.js